import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { InvoiceStatus } from '@models/invoice.model';
import {
  CreateInvoiceDraftInput,
  GetPaginatedInvoicesInput,
  UpdateInvoiceDraftInput,
  invoiceService,
} from '@services/invoice';
import { PENDING_INVOICES_COUNT_QUERY_KEY } from '@hooks/api/activity/usePendingInvoicesCount';
import { useTranslation } from '@hooks/useTranslation';

export const INVOICE_QUERY_KEY = 'invoice';
export const INVOICES_BY_PROJECT_QUERY_KEY = 'invoicesByProject';
export const PAGINATED_INVOICES_QUERY_KEY = 'paginatedInvoices';
export const AVAILABLE_BUDGET_ITEMS_QUERY_KEY = 'availableBudgetItemsForInvoice';

export const useGetInvoice = (invoiceId?: string | null) => {
  return useQuery({
    queryKey: [INVOICE_QUERY_KEY, invoiceId],
    queryFn: () => {
      if (!invoiceId) return null;
      return invoiceService.getInvoice(invoiceId);
    },
    enabled: !!invoiceId,
  });
};

export const useGetInvoicesByProject = (projectId?: string | null, statuses?: InvoiceStatus[]) => {
  return useQuery({
    queryKey: [INVOICES_BY_PROJECT_QUERY_KEY, projectId, statuses],
    queryFn: () => {
      if (!projectId) return [];
      return invoiceService.getInvoicesByProject(projectId, statuses);
    },
    enabled: !!projectId,
  });
};

export const useGetPaginatedInvoices = (
  input: GetPaginatedInvoicesInput,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [PAGINATED_INVOICES_QUERY_KEY, input],
    queryFn: () => invoiceService.getPaginatedInvoices(input),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
};

export const useGetAvailableBudgetItems = (projectId?: string | null) => {
  return useQuery({
    queryKey: [AVAILABLE_BUDGET_ITEMS_QUERY_KEY, projectId],
    queryFn: () => {
      if (!projectId) {
        return {
          materials: [],
          equipment: [],
          expenses: [],
          delegatedTaskCosts: [],
        };
      }
      return invoiceService.getAvailableBudgetItems(projectId);
    },
    enabled: !!projectId,
  });
};

const invalidateInvoiceCaches = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [INVOICE_QUERY_KEY], refetchType: 'active' });
  queryClient.invalidateQueries({ queryKey: [INVOICES_BY_PROJECT_QUERY_KEY], refetchType: 'active' });
  queryClient.invalidateQueries({ queryKey: [PAGINATED_INVOICES_QUERY_KEY], refetchType: 'active' });
  queryClient.invalidateQueries({ queryKey: [AVAILABLE_BUDGET_ITEMS_QUERY_KEY], refetchType: 'active' });
};

// Sending or voiding an invoice fires backend `evaluateInvoicePendingForTask` for every
// task that had items in it. That can resolve existing INVOICE_PENDING activities (items
// got billed) or create new ones (void-from-SENT released items on a completed task).
// Every activity queryKey starts with 'activities', so a prefix-invalidate refetches
// every active activity query in one shot (dashboard pinned-pending row, project feed,
// task feed, etc.) without us having to import each key constant. The dashboard's
// invoice-triage pill is keyed separately ('pending-invoices-count'), so invalidate it
// too — otherwise the alert count keeps showing stale numbers after an invoice resolves
// or voids the underlying INVOICE_PENDING activities.
const invalidateActivities = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['activities'], refetchType: 'active' });
  queryClient.invalidateQueries({ queryKey: [PENDING_INVOICES_COUNT_QUERY_KEY], refetchType: 'active' });
};

const errorMessage = (error: any, fallback: string): string => {
  return error?.response?.errors?.[0]?.message || error?.message || fallback;
};

export const useCreateInvoiceDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceDraftInput) => invoiceService.createInvoiceDraft(input),
    onSuccess: () => invalidateInvoiceCaches(queryClient),
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Error creating invoice draft'));
    },
  });
};

export const useUpdateInvoiceDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInvoiceDraftInput) => invoiceService.updateInvoiceDraft(input),
    onSuccess: () => invalidateInvoiceCaches(queryClient),
    onError: (error: any) => {
      const message = errorMessage(error, 'Failed to save changes');
      if (!String(message).toLowerCase().includes('was modified elsewhere')) {
        toast.error(message);
      }
    },
  });
};

export const useDeleteInvoiceDraft = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => invoiceService.deleteInvoiceDraft(invoiceId),
    onSuccess: () => {
      toast.success(tActionMessages.draftDeleted);
      invalidateInvoiceCaches(queryClient);
    },
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Failed to delete draft'));
    },
  });
};

export const useSendInvoice = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => invoiceService.sendInvoice(invoiceId),
    onSuccess: (invoice) => {
      if (invoice?.invoiceNumber) {
        toast.success(`Invoice ${invoice.invoiceNumber} sent`);
      } else {
        toast.success(tActionMessages.invoiceSent);
      }
      invalidateInvoiceCaches(queryClient);
      invalidateActivities(queryClient);
    },
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Failed to send invoice'));
    },
  });
};

export const useVoidInvoice = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: string; reason?: string }) =>
      invoiceService.voidInvoice(invoiceId, reason),
    onSuccess: () => {
      toast.success(tActionMessages.invoiceVoided);
      invalidateInvoiceCaches(queryClient);
      invalidateActivities(queryClient);
    },
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Failed to void invoice'));
    },
  });
};

/**
 * Silently reconciles a single invoice's status against NMI. Used as a recovery
 * path for missed webhooks — fires on render when displaying a SENT invoice so
 * the badge doesn't lie when NMI already marked it paid/voided.
 *
 * No toasts on either side: success is reflected by the refreshed cache, failures
 * stay invisible (we'll just keep showing the stored status until the next try
 * or the daily reminder cron picks it up).
 */
export const useRefreshInvoiceStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => invoiceService.refreshInvoiceStatus(invoiceId),
    onSuccess: (invoice) => {
      invalidateInvoiceCaches(queryClient);
      // Only ripple to activities when the reconciliation actually moved off SENT —
      // a SENT→VOIDED transition releases items and may re-open INVOICE_PENDING. We
      // fan this mutation out across every SENT invoice on slider open, so skipping
      // the activities invalidation for unchanged responses avoids N redundant refetches.
      if (invoice && invoice.status !== InvoiceStatus.SENT) {
        invalidateActivities(queryClient);
      }
    },
  });
};

export const useResendInvoiceEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      recipientEmail,
    }: {
      invoiceId: string;
      recipientEmail?: string;
    }) => invoiceService.resendInvoiceEmail(invoiceId, recipientEmail),
    onSuccess: (invoice) => {
      toast.success(`Invoice email resent to ${invoice.recipientEmail}`);
      invalidateInvoiceCaches(queryClient);
    },
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Failed to resend invoice email'));
    },
  });
};
